"use client";

import { ArrowUpRightIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ComponentPropsWithoutRef, FC } from "react";
import {
  languageSwitchHref,
  Locale,
  LOCALE_NAMES,
  LOCALES,
} from "../const/i18n";
import { useLocale, useLocalizedHref, useMessages } from "../context/i18n";
import { footer as enFooter } from "../dictionaries/en/footer";
import Logo from "./Logo";
import { DISCORD_LINK } from "./Nav";
import Typography from "./Typography";

// The /alternatives hub links every rival, so this column keeps the ones worth a
// sitewide link in tier 1: Ads conversions and AI answers first, then the pages
// already getting Google clicks there (Spikes Studio, VEED, WayinVideo).
const RIVAL_LINKS = [
  { slug: "opus-clip", label: "OpusClip" },
  { slug: "ssemble", label: "Ssemble" },
  { slug: "viblo", label: "Viblo" },
  { slug: "vizard-ai", label: "Vizard" },
  { slug: "capcut", label: "CapCut" },
  { slug: "descript", label: "Descript" },
  { slug: "submagic", label: "Submagic" },
  { slug: "spikes-studio", label: "Spikes Studio" },
  { slug: "veed-io", label: "VEED" },
  { slug: "wayin", label: "WayinVideo" },
  { slug: "repurpose-io", label: "Repurpose.io" },
  { slug: "riverside", label: "Riverside" },
  { slug: "streamladder", label: "StreamLadder" },
];

// Rival pages that only exist in a language, so no other page links them.
const LOCALE_ONLY_RIVAL_LINKS: Partial<Record<Locale, typeof RIVAL_LINKS>> = {
  fr: [{ slug: "remakeit", label: "RemakeIt" }],
};

type FooterProps = {} & ComponentPropsWithoutRef<"footer">;

const Footer: FC<FooterProps> = ({ ...rest }) => {
  const lang = useLocale();
  const m = useMessages("footer", enFooter);
  const href = useLocalizedHref();
  const pathname = usePathname() ?? "/";
  const rivalLinks = [...(LOCALE_ONLY_RIVAL_LINKS[lang] ?? []), ...RIVAL_LINKS];

  return (
    <footer
      {...rest}
      className={`${rest.className} py-16 max-w-[1500px] px-3 md:px-12 text-center mx-auto`}
    >
      <Typography className=" text-slate-700">
        {m.madeWithBy}{" "}
        {/* `underline` is required by Lighthouse a11y audit
            `link-in-text-block` (links inside paragraphs must not rely on
            color alone to be distinguishable). Removing it costs 7 a11y
            points. */}
        <a
          className="text-slate-500 underline underline-offset-2"
          href="https://twitter.com/deletethistheo"
          target="_blank"
          rel="noreferrer"
        >
          @theo
        </a>{" "}
        {m.and}{" "}
        <a
          className="text-slate-500 underline underline-offset-2"
          href="https://twitter.com/VictorTimsit"
          target="_blank"
          rel="noreferrer"
        >
          @victor
        </a>
      </Typography>

      <div className="mt-16 text-slate-700 flex flex-col md:flex-row flex-wrap gap-16 justify-between text-left">
        {/* <div>
          <Typography variant="caption" className="font-medium">
            Business Registry Number
          </Typography>
          <Typography variant="caption" className="">
            SIRET: 88806141300017
          </Typography>
          <Typography variant="caption" className="">
            SIREN (HQ): 888061413
          </Typography>
        </div>

        <div>
          <Typography variant="caption" className="font-medium">
            Business Address
          </Typography>
          <Typography variant="caption" className="">
            65 rue Roger François
          </Typography>
          <Typography variant="caption" className="">
            94700, Maisons-Alfort, France
          </Typography>
        </div> */}
        <div className="flex flex-col gap-1 items-baseline justify-start text-slate-700 text-sm flex-1">
          <Typography variant="subtitle3">
            {m.company}
          </Typography>

          <Link href="/terms-of-services" className="text-slate-500">
            {m.terms}
          </Link>
          <Link href="/privacy-policy" className="text-slate-500">
            {m.privacy}
          </Link>
          {LOCALES.filter((other) => other !== lang).map((other) => (
            <Link
              key={other}
              href={languageSwitchHref(pathname, other)}
              hrefLang={other}
              lang={other}
              className="text-slate-500"
            >
              {LOCALE_NAMES[other]}
            </Link>
          ))}
          <Logo className="mt-12 md:mt-auto" />
          <Typography className="mt-1" variant="subtitle3">
            {m.tagline}
          </Typography>
        </div>
        <div className="flex flex-col gap-1 items-baseline justify-start text-slate-700 text-sm">
          <Typography variant="subtitle3">{m.resources}</Typography>
          <Link href={href("/blog")} className="text-slate-500">
            {m.blog}
          </Link>
          <Link href={href("/tools")} className="text-slate-500">
            {m.tools}
          </Link>
          {/* <Link href="/briefs" className="text-slate-500">
            Briefs
          </Link> */}
          <Link
            target="_blank"
            href="https://docs.klap.app"
            className="text-slate-500 flex items-center justify-center gap-1"
          >
            <span>{m.apiDocs}</span>
            <ArrowUpRightIcon className="w-4 h-4" />
          </Link>
          <Link
            target="_blank"
            href={DISCORD_LINK}
            className="text-slate-500 flex items-center justify-center gap-1"
          >
            <span>{m.discord}</span>
            <ArrowUpRightIcon className="w-4 h-4" />
          </Link>
          <Link href={href("/for/clippers")} className="text-slate-500">
            {m.forClippers}
          </Link>
        </div>
        <div className="flex flex-col gap-1 items-baseline justify-start text-slate-700 text-sm">
          <Typography variant="subtitle3">{m.services}</Typography>
          <Link href={href("/affiliate")} className="text-slate-500">
            {m.becomeAffiliate}
          </Link>
          <Link href={href("/api")} className="text-slate-500">
            {m.api}
          </Link>
          <Link href={href("/mcp")} className="text-slate-500">
            {m.mcp}
          </Link>
          <Link href={href("/integrations")} className="text-slate-500">
            {m.integrations}
          </Link>
          <Link href={href("/integrations/zapier")} className="text-slate-500">
            {m.zapier}
          </Link>
          <Link href={href("/integrations/make")} className="text-slate-500">
            {m.make}
          </Link>
          <Link href={href("/integrations/n8n")} className="text-slate-500">
            {m.n8n}
          </Link>
        </div>
        <div className="flex flex-col gap-1 items-baseline justify-start text-slate-700 text-sm">
          <Typography variant="subtitle3">{m.alternatives}</Typography>
          <Link href={href("/alternatives")} className="text-slate-500">
            {m.allAlternatives}
          </Link>
          {rivalLinks.map(({ slug, label }) => (
            <Link
              key={slug}
              href={href(`/alternatives/${slug}`)}
              className="text-slate-500"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
